import { useEffect } from "react";
import { goLive } from "../booth/viewer.js";
import { TEAM } from "../booth/teams.js";
import {
  ET,
  clockParts,
  dayWords,
  scoreWords,
  seriesView,
  standingWords,
  timeET,
  timeLocal,
  useNow,
  useSeries,
} from "../booth/series.js";

// The home page between World Series games: the stage in the hero where his
// live picture goes (hero-live.jsx) holds a scoreboard counting down to first
// pitch, with the matchup, the park and the channel, and a bar into the booth.
// While a game's on and he isn't, it shows the score instead. On the air,
// hero-live takes over; once the Series is decided the stage is gone and the
// hero is itself again (the "Greg's booth" section tells the ending).

function Chip({ abbr }) {
  const [t, t2] = TEAM[abbr] || ["#1a3658", "#f4f1ea"];
  return (
    <span className="hn-chip" style={{ "--t": t, "--t2": t2 }}>
      {abbr}
    </span>
  );
}

const hourET = (at) =>
  parseInt(
    new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: ET }).format(
      new Date(at),
    ),
    10,
  );

const channel = (tv) => (tv ? `on ${String(tv).split("/")[0].trim()}` : "");

function Play() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}

function Arrow() {
  return (
    <svg className="hn-cta-arrow" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 12h13M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function HeroNext({ live, onStage }) {
  const data = useSeries();
  const now = useNow(1000);
  const v = seriesView(data, now);
  const showing = !live && (v.phase === "next" || v.phase === "on");
  useEffect(() => {
    onStage?.(showing);
  }, [showing, onStage]);
  if (!showing) return null;
  return v.phase === "on" ? <GameOn v={v} /> : <Countdown v={v} now={now} />;
}

function Countdown({ v, now }) {
  const g = v.game;
  const ms = Date.parse(g.at) - now;
  const postponed = g.state === "postponed";
  // Past the listed time with the feed still saying "scheduled": any minute.
  const any = !postponed && ms <= 0;
  const soon = !postponed && ms < 3600_000;
  const c = clockParts(ms);
  const day = dayWords(g.at, now);
  const sign = postponed
    ? "Postponed"
    : any
      ? "First pitch"
      : soon
        ? "First pitch soon"
        : day === "Today"
          ? hourET(g.at) >= 17
            ? "Tonight"
            : "Today"
          : day === "Tomorrow"
            ? "Tomorrow"
            : "Next game";
  // "Tonight" on the sign and "Today" on the chip would say it twice.
  const named = sign !== "Next game";
  const when = g.tbd ? `${day} · time TBA` : named ? timeET(g.at) : `${day} · ${timeET(g.at)}`;
  const local = g.tbd ? "" : timeLocal(g.at);
  const standing = standingWords(v);
  const where = [g.venue, channel(g.tv), local ? `${local} your time` : ""]
    .filter(Boolean)
    .join(" · ");
  const label = `${v.series.name} Game ${g.no}, ${g.away.club} at ${g.home.club}, ${when}. Greg calls it live in his booth.`;
  return (
    <a className={`hn${soon ? " is-soon" : ""}`} href="/live" onClick={goLive} aria-label={label}>
      <span className="hn-stage">
        <span className="hn-sign">
          <span className="hn-sign-main">
            <i aria-hidden="true" />
            {sign}
          </span>
          <span className="hn-sign-when">{when}</span>
        </span>
        <span className="hn-board">
          <span className="hn-row hn-head">
            <span className="hn-series">
              {v.series.name} · Game {g.no}
              {g.ifNecessary ? " · if needed" : ""}
            </span>
            {standing && <span className="hn-standing">{standing}</span>}
          </span>
          <span className="hn-matchup">
            <span className="hn-team">
              <Chip abbr={g.away.abbr} />
              <b>{g.away.club}</b>
            </span>
            <span className="hn-at">at</span>
            <span className="hn-team">
              <Chip abbr={g.home.abbr} />
              <b>{g.home.club}</b>
            </span>
          </span>
          {postponed ? (
            <span className="hn-note">
              {g.status && g.status !== "Postponed" ? g.status : "Rained out"}. A new time is
              coming.
            </span>
          ) : any ? (
            <span className="hn-note">Any minute now.</span>
          ) : (
            <span
              className="hn-clock"
              role="timer"
              aria-label={`${c.d} days, ${c.h} hours, ${c.m} minutes to first pitch`}
            >
              {c.d > 0 && (
                <>
                  <span className="hn-cell">
                    <b>{c.d}</b>
                    <small>{c.d === 1 ? "day" : "days"}</small>
                  </span>
                  <span className="hn-colon">:</span>
                </>
              )}
              <span className="hn-cell">
                <b>{c.h}</b>
                <small>hrs</small>
              </span>
              <span className="hn-colon">:</span>
              <span className="hn-cell">
                <b>{c.m}</b>
                <small>min</small>
              </span>
              <span className="hn-colon">:</span>
              <span className="hn-cell">
                <b>{c.s}</b>
                <small>sec</small>
              </span>
            </span>
          )}
          <span className="hn-row hn-foot">
            {where && <span className="hn-where">{where}</span>}
            <span className="hn-greg">
              <i aria-hidden="true" />
              {any ? "Greg's booth opens any minute" : "Greg's in the booth before first pitch"}
            </span>
          </span>
        </span>
      </span>
      <span className="hn-cta">
        <span className="hn-cta-play" aria-hidden="true">
          <Play />
        </span>
        <span className="hn-cta-text">
          <b>Greg calls it live, pitch by pitch</b>
          <span>
            {v.last
              ? `Game ${v.last.no}: ${scoreWords(v.last)}`
              : "Free · Nothing to install · Pull up a seat"}
          </span>
        </span>
        <Arrow />
      </span>
    </a>
  );
}

// The game's on, Greg isn't (yet): the score, and the way in.
function GameOn({ v }) {
  const g = v.game;
  const standing = standingWords(v);
  const where = [g.venue, channel(g.tv)].filter(Boolean).join(" · ");
  return (
    <a
      className="hn is-on"
      href="/live"
      onClick={goLive}
      aria-label={`${v.series.name} Game ${g.no} is on: ${g.away.club} ${g.away.score ?? 0}, ${g.home.club} ${g.home.score ?? 0}. Go to Greg's booth.`}
    >
      <span className="hn-stage">
        <span className="hn-sign">
          <span className="hn-sign-main">
            <i aria-hidden="true" />
            Game on
          </span>
          <span className="hn-sign-when">{g.inning || "In progress"}</span>
        </span>
        <span className="hn-board">
          <span className="hn-row hn-head">
            <span className="hn-series">
              {v.series.name} · Game {g.no}
            </span>
            {standing && <span className="hn-standing">{standing}</span>}
          </span>
          <span className="hn-score">
            <span className="hn-score-row">
              <Chip abbr={g.away.abbr} />
              <span className="hn-score-club">{g.away.club}</span>
              <b>{g.away.score ?? 0}</b>
            </span>
            <span className="hn-score-row">
              <Chip abbr={g.home.abbr} />
              <span className="hn-score-club">{g.home.club}</span>
              <b>{g.home.score ?? 0}</b>
            </span>
          </span>
          <span className="hn-row hn-foot">
            {where && <span className="hn-where">{where}</span>}
            <span className="hn-greg">
              <i aria-hidden="true" />
              Greg's booth opens when he's on
            </span>
          </span>
        </span>
      </span>
      <span className="hn-cta">
        <span className="hn-cta-play" aria-hidden="true">
          <Play />
        </span>
        <span className="hn-cta-text">
          <b>Go to Greg's booth</b>
          <span>The scoreboard's up while you wait</span>
        </span>
        <Arrow />
      </span>
    </a>
  );
}

const css = `
/* The stage: a scoreboard where his live picture goes, the sign on top and
   the bar under it. hero.jsx puts it in the right column of the hero. */
.hn {
  position: relative; z-index: 6;
  display: grid; gap: 18px;
  width: 100%;
  color: var(--bone);
  text-decoration: none;
  animation: hn-in 800ms cubic-bezier(0.2, 0.9, 0.2, 1) both;
}
@keyframes hn-in { from { opacity: 0; translate: 28px 0; } }
.hn:focus-visible { outline: none; }
.hn:focus-visible .hn-board { box-shadow: 0 0 0 3px var(--gold-bright), 0 40px 90px -40px rgba(0, 0, 0, 0.95); }
.hn-stage {
  position: relative; display: block;
  width: 100%;
  /* Never taller than the hero has room for. */
  max-width: max(460px, calc((100vh - 420px) * 1.6));
  margin-left: auto;
  padding-top: 14px;
}
/* A thin ring, gold to blue, turning slowly around the board. */
.hn-stage::before {
  content: ""; position: absolute; left: -3px; right: -3px; top: 11px; bottom: -3px;
  border-radius: 21px; padding: 3px;
  background: conic-gradient(from var(--hn-a), rgba(43, 138, 255, 0.1), #2b8aff 14%, #f2c27b 30%, rgba(43, 138, 255, 0.1) 50%, rgba(43, 138, 255, 0.1));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  animation: hn-ring 12s linear infinite;
  pointer-events: none;
}
@property --hn-a { syntax: "<angle>"; inherits: false; initial-value: 0deg; }
@keyframes hn-ring { to { --hn-a: 360deg; } }

/* The board itself: dark glass, a gold hairline, a faint scanline. */
.hn-board {
  position: relative; display: grid; gap: 20px;
  padding: 28px 30px 24px;
  border-radius: 18px;
  background:
    radial-gradient(90% 70% at 50% 0%, rgba(0, 70, 135, 0.5), transparent 62%),
    linear-gradient(180deg, #091628 0%, #050d1a 100%);
  box-shadow:
    inset 0 0 0 1px rgba(189, 155, 96, 0.42),
    inset 0 0 80px rgba(0, 0, 0, 0.45),
    0 0 60px -14px rgba(43, 138, 255, 0.45),
    0 40px 90px -40px rgba(0, 0, 0, 0.95);
  overflow: hidden;
  transition: box-shadow 220ms ease;
}
.hn-board::after {
  content: ""; position: absolute; inset: 0;
  background: repeating-linear-gradient(180deg, rgba(255, 255, 255, 0.028) 0 1px, transparent 1px 3px);
  pointer-events: none;
}
.hn:hover .hn-board { box-shadow: inset 0 0 0 1px rgba(212, 182, 122, 0.7), inset 0 0 80px rgba(0, 0, 0, 0.45), 0 0 80px -14px rgba(43, 138, 255, 0.6), 0 40px 90px -40px rgba(0, 0, 0, 0.95); }
.hn-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px 18px; flex-wrap: wrap; }
.hn-series { font: 600 0.6875rem/1 var(--mono); letter-spacing: 0.18em; text-transform: uppercase; color: var(--gold); }
.hn-standing { font: 500 0.6875rem/1 var(--mono); letter-spacing: 0.12em; text-transform: uppercase; color: var(--bone-dim); }

.hn-matchup { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.hn-team { display: inline-flex; align-items: center; gap: 10px; font: 500 clamp(1.375rem, 2vw, 1.875rem)/1 var(--serif); letter-spacing: -0.01em; }
.hn-at { font: 500 0.6875rem/1 var(--mono); letter-spacing: 0.18em; text-transform: uppercase; color: rgba(244, 241, 234, 0.5); }
.hn-chip { display: inline-grid; place-items: center; min-width: 2.9em; padding: 0.3em 0.45em; border-radius: 3px; background: var(--t); color: var(--t2); font: 700 0.6875rem/1 var(--sans); letter-spacing: 0.05em; }

/* The clock: scoreboard bulbs. */
.hn-clock { display: flex; align-items: flex-start; gap: 4px 8px; }
.hn-cell { display: grid; justify-items: center; gap: 8px; min-width: 2.1em; }
.hn-cell b {
  font: 400 clamp(3.5rem, 6.2vw, 5.5rem)/0.82 var(--crt);
  color: #ffd98a;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 10px rgba(255, 196, 86, 0.55), 0 0 30px rgba(255, 170, 60, 0.28);
}
.hn-cell small { font: 500 0.5625rem/1 var(--mono); letter-spacing: 0.22em; text-transform: uppercase; color: rgba(244, 241, 234, 0.5); }
.hn-colon { font: 400 clamp(3.5rem, 6.2vw, 5.5rem)/0.82 var(--crt); color: rgba(255, 217, 138, 0.5); animation: hn-tick 1s steps(2, jump-none) infinite; }
@keyframes hn-tick { 50% { opacity: 0.25; } }
.hn-note { font: 400 clamp(1.5rem, 2.4vw, 2.25rem)/1.1 var(--crt); color: #ffd98a; letter-spacing: 0.03em; text-shadow: 0 0 10px rgba(255, 196, 86, 0.5); }
/* Under an hour the bulbs go red. */
.hn.is-soon .hn-cell b, .hn.is-soon .hn-note { color: #ff8a80; text-shadow: 0 0 10px rgba(255, 74, 61, 0.6), 0 0 30px rgba(255, 74, 61, 0.3); }
.hn.is-soon .hn-colon { color: rgba(255, 138, 128, 0.5); }

/* The score, while the game's on and Greg isn't. */
.hn-score { display: grid; gap: 10px; }
.hn-score-row { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 14px; }
.hn-score-club { font: 500 clamp(1.25rem, 1.8vw, 1.625rem)/1 var(--serif); }
.hn-score-row b { font: 400 clamp(2.75rem, 4.6vw, 4rem)/0.82 var(--crt); color: #ffd98a; text-shadow: 0 0 10px rgba(255, 196, 86, 0.55); font-variant-numeric: tabular-nums; }

.hn-foot { font: 500 0.6875rem/1.45 var(--mono); letter-spacing: 0.08em; color: var(--bone-dim); align-items: center; }
.hn-where { min-width: 0; }
.hn-greg { display: inline-flex; align-items: center; gap: 8px; font-size: 0.625rem; letter-spacing: 0.14em; text-transform: uppercase; color: #ff8a80; white-space: nowrap; }
.hn-greg i { width: 7px; height: 7px; border-radius: 50%; background: #ff4a3d; box-shadow: 0 0 0 0 rgba(255, 74, 61, 0.6); animation: hn-pulse 1.6s ease-in-out infinite; }
@keyframes hn-pulse { 50% { box-shadow: 0 0 0 6px rgba(255, 74, 61, 0); } }

/* The sign, over the top edge: what's next, and when. */
.hn-sign { position: absolute; z-index: 4; top: 0; left: 26px; display: inline-flex; filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.5)); }
.hn-sign-main { display: inline-flex; align-items: center; gap: 9px; padding: 9px 14px 9px 12px; border-radius: 8px 0 0 8px; background: linear-gradient(180deg, #f2c27b, #bd9b60); color: var(--navy-900); font: 800 0.8125rem/1 var(--mono); letter-spacing: 0.22em; text-transform: uppercase; box-shadow: inset 0 0 0 1px rgba(255, 240, 200, 0.5), 0 0 26px rgba(242, 194, 123, 0.35); white-space: nowrap; }
.hn-sign-main i { width: 8px; height: 8px; border-radius: 50%; background: var(--navy-900); opacity: 0.75; }
.hn-sign-when { display: inline-flex; align-items: center; padding: 9px 12px; border-radius: 0 8px 8px 0; background: rgba(5, 13, 26, 0.94); color: var(--bone); font: 600 0.8125rem/1 var(--mono); letter-spacing: 0.08em; font-variant-numeric: tabular-nums; box-shadow: inset 0 0 0 1px rgba(189, 155, 96, 0.55); white-space: nowrap; }
.hn.is-soon .hn-sign-main, .hn.is-on .hn-sign-main { background: linear-gradient(180deg, #ff5a4c, #c8102e); color: #fff; text-shadow: 0 0 12px rgba(255, 200, 190, 0.8); box-shadow: inset 0 0 0 1px rgba(255, 150, 140, 0.55), 0 0 26px rgba(255, 74, 61, 0.55); animation: hn-sign 2.4s ease-in-out infinite; }
.hn.is-soon .hn-sign-main i, .hn.is-on .hn-sign-main i { background: #fff; opacity: 1; box-shadow: 0 0 10px #fff; animation: hn-blink 1.3s steps(2, jump-none) infinite; }
.hn.is-soon .hn-sign-when, .hn.is-on .hn-sign-when { box-shadow: inset 0 0 0 1px rgba(255, 74, 61, 0.55); }
@keyframes hn-sign { 50% { box-shadow: inset 0 0 0 1px rgba(255, 150, 140, 0.55), 0 0 44px rgba(255, 74, 61, 0.85); } }
@keyframes hn-blink { 50% { opacity: 0.25; } }

/* The bar under the board: the one thing to press. */
.hn-cta {
  position: relative; overflow: hidden;
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 16px;
  max-width: max(460px, calc((100vh - 420px) * 1.6)); margin-left: auto; width: 100%;
  padding: 12px 20px 12px 12px;
  border-radius: 16px;
  background: linear-gradient(135deg, #2b8aff 0%, #0064c2 55%, #004687 100%);
  box-shadow: inset 0 0 0 1px rgba(140, 190, 255, 0.45), 0 22px 44px -18px rgba(43, 138, 255, 0.7);
  transition: transform 220ms ease, box-shadow 220ms ease;
}
.hn:hover .hn-cta { transform: translateY(-2px); box-shadow: inset 0 0 0 1px rgba(170, 210, 255, 0.6), 0 28px 54px -18px rgba(43, 138, 255, 0.9); }
.hn-cta::after { content: ""; position: absolute; top: -40%; bottom: -40%; left: -30%; width: 22%; background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.28), transparent); transform: skewX(-18deg); animation: hn-shine 6s ease-in-out infinite; pointer-events: none; }
@keyframes hn-shine { 0%, 60% { left: -30%; } 100% { left: 130%; } }
.hn-cta-play { display: grid; place-items: center; width: 52px; height: 52px; border-radius: 50%; background: #fff; color: #004687; box-shadow: 0 8px 20px -8px rgba(0, 0, 0, 0.6); transition: transform 220ms ease; }
.hn-cta-play svg { width: 26px; height: 26px; margin-left: 3px; }
.hn:hover .hn-cta-play { transform: scale(1.06); }
.hn-cta-text { display: grid; gap: 3px; min-width: 0; color: #fff; }
.hn-cta-text b { font: 700 clamp(1.0625rem, 1.4vw, 1.3125rem)/1.1 var(--sans); letter-spacing: -0.01em; }
.hn-cta-text span { font: 500 0.6875rem/1.3 var(--mono); letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255, 255, 255, 0.82); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hn-cta-arrow { width: 26px; height: 26px; color: #fff; transition: transform 220ms ease; }
.hn:hover .hn-cta-arrow { transform: translateX(4px); }
.hn.is-on .hn-cta { background: linear-gradient(135deg, #ff5d4f 0%, #c8102e 55%, #a30c27 100%); box-shadow: inset 0 0 0 1px rgba(255, 140, 130, 0.45), 0 22px 44px -18px rgba(255, 74, 61, 0.8); }
.hn.is-on .hn-cta-play { color: #c8102e; }

/* Narrower screens: the board takes the top of the hero, full width. */
@media (max-width: 1080px) {
  .hn-board { padding: 22px 22px 18px; gap: 16px; }
  .hn-cell b, .hn-colon { font-size: clamp(2.75rem, 5.5vw, 4rem); }
}
@media (max-width: 860px) {
  .hn { gap: 10px; max-width: 560px; margin: 0 auto 16px; animation-name: hn-in-flow; }
  @keyframes hn-in-flow { from { opacity: 0; translate: 0 12px; } }
  .hn-stage, .hn-cta { max-width: none; margin-left: 0; }
  .hn-stage { padding-top: 11px; }
  .hn-stage::before { top: 8px; }
  .hn-board { padding: 22px 16px 14px; gap: 12px; border-radius: 14px; }
  .hn-sign { left: 14px; }
  .hn-sign-main { padding: 7px 10px 7px 9px; font-size: 0.6875rem; letter-spacing: 0.18em; }
  .hn-sign-when { padding: 7px 9px; font-size: 0.6875rem; }
  .hn-head { display: none; }
  .hn-team { font-size: 1.125rem; gap: 8px; }
  .hn-matchup { gap: 10px; }
  .hn-cell { gap: 5px; min-width: 1.9em; }
  .hn-cell b, .hn-colon { font-size: 2.75rem; }
  .hn-cell small { font-size: 0.5rem; letter-spacing: 0.16em; }
  .hn-note { font-size: 1.5rem; }
  .hn-score-row b { font-size: 2.5rem; }
  .hn-score-club { font-size: 1.125rem; }
  .hn-foot { font-size: 0.625rem; gap: 6px 14px; }
  .hn-cta { gap: 12px; padding: 10px 16px 10px 10px; border-radius: 14px; }
  .hn-cta-play { width: 42px; height: 42px; }
  .hn-cta-play svg { width: 20px; height: 20px; }
  .hn-cta-text b { font-size: 1rem; }
  .hn-cta-text span { font-size: 0.625rem; }
  .hn-cta-arrow { width: 22px; height: 22px; }
}
@media (max-width: 480px) {
  .hn { margin-bottom: 12px; }
  .hn-where { display: none; }
  .hn-cta-text span { display: none; }
  .hn-cta { padding: 9px 14px 9px 9px; }
  .hn-cell b, .hn-colon { font-size: 2.375rem; }
}
@media (prefers-reduced-motion: reduce) {
  .hn, .hn * { animation: none !important; transition: none !important; }
  .hn-cta::after { display: none; }
}
`;
if (typeof document !== "undefined") {
  const el = document.createElement("style");
  el.dataset.section = "hero-next";
  el.textContent = css;
  document.head.appendChild(el);
}
